import {
    Container,
    Typography,
    Paper,
    Box,
    Grid,
    Divider,
    Button,
    Rating,
    Backdrop,
    CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import './riepilogoValutazioneIntermedia.css';
import { formGradimentoService } from '../../services/FormGradimento/FormGradimentoService';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const RiepilogoValutazioneIntermedia = () => {
    const { idUtente } = useAuth();
    const navigate = useNavigate();

    const [loading, setStatoCaricamento] = useState(false);
    const [messaggioCaricamento, setMessaggioCaricamento] = useState("");
    const [datiRiepilogo, setDatiRiepilogo] = useState(null);
    
    // Funzione per impostare lo stato di caricamento
    const setCaricamento = (isLoading, messaggio = "") => {
        setStatoCaricamento(isLoading);
        setMessaggioCaricamento(messaggio);
    };

    // Per formattare dal formato backend (ISO) al formato visualizzato
    const formattaData = (dataString) => {
        if (!dataString) return '-';
        try {
            return format(new Date(dataString), 'dd/MM/yyyy', { locale: it });
        } catch (e) {
            return '-';
        }
    };

    useEffect(() => {
        const caricaDati = async () => {
            try {
                setCaricamento(true, "Caricamento dati...");

                // Chiamata al servizio per recuperare i dati della valutazione precedente
                const datiRicevuti = await formGradimentoService.getRiepilogoValutazione();
                
                setDatiRiepilogo(datiRicevuti);

            } catch (errore) {
                console.error('Errore nel caricamento dei dati:', errore);
                // In caso di errore, torna alla homepage
                //navigate('/homepage');
            } finally {
                setCaricamento(false);
            }
        };

        // Esegui la funzione di caricamento
        caricaDati();
    }, [idUtente, navigate]);

    // Funzione per tornare alla homepage
    const tornaAllaHomepage = () => {
        navigate('/homepage');
    };

    // Funzione per convertire il valore numerico in testo
    const valutazioneInTesto = (valore) => {
        if (!valore) return '-';
        
        const valoreNumerico = parseInt(valore);
        switch (valoreNumerico) {
            case 1: return 'Molto in disaccordo';
            case 2: return 'Disaccordo';
            case 3: return 'Neutro';
            case 4: return 'D\'accordo';
            case 5: return 'Molto d\'accordo';
            default: return '-';
        }
    };

    // Componente per visualizzare una valutazione
    const VisualizzaValutazione = ({ etichetta, valore }) => (
        <Box mb={2}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom className="etichetta-domanda">
                {etichetta}
            </Typography>
            <Box display="flex" alignItems="center">
                {/*<Rating
                    value={parseInt(valore) || 0}
                    readOnly
                    max={5}
                    size="medium"
                />*/}
                <Typography variant="body1" ml={1}>
                    {valutazioneInTesto(valore)}
                </Typography>
            </Box>
        </Box>
    );

    // Componente per visualizzare una risposta testuale
    const VisualizzaRisposta = ({ etichetta, valore }) => (
        <Box mb={3}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom className="etichetta-domanda">
                {etichetta}
            </Typography>
            <Paper variant="outlined" className="risposta-testuale">
                <Typography variant="body1">
                    {valore || '-'}
                </Typography>
            </Paper>
        </Box>
    );

    if (!datiRiepilogo && !loading) {
        return null; // Non mostrare nulla durante il primo caricamento
    }

    return (
        <Container maxWidth="md" className="form-container">
            <Typography variant="h4" component="h1" align="center" gutterBottom className="titolo">
                Riepilogo Valutazione Intermedia
            </Typography>

            <Typography variant="subtitle1" align="center" paragraph className="sottotitolo">
                Grazie per aver completato il questionario di valutazione. Ecco un riepilogo delle tue risposte.
            </Typography>

            {datiRiepilogo && (
                <Paper elevation={3} className="paper">
                    {/* Sezione Informazioni Personali */}
                    <Box mb={4}>
                        <Typography variant="h6" gutterBottom>
                            Informazioni Generali
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="textSecondary">Nome del Partecipante</Typography>
                                <Typography variant="body1">{datiRiepilogo.nomeDelPartecipante || '-'}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="textSecondary">Numero Progetto</Typography>
                                <Typography variant="body1">{datiRiepilogo.numeroProgetto || '-'}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="textSecondary">Destinazione</Typography>
                                <Typography variant="body1">{datiRiepilogo.destinazione || '-'}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="textSecondary">Data Inizio Periodo</Typography>
                                <Typography variant="body1">{formattaData(datiRiepilogo.dataInizioPeriodo)}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="textSecondary">Data Fine Periodo</Typography>
                                <Typography variant="body1">{formattaData(datiRiepilogo.dataFinePeriodo)}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="textSecondary">Valutazione Periodo Da</Typography>
                                <Typography variant="body1">{formattaData(datiRiepilogo.dataValutazionePeriodoDa)}</Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="textSecondary">Valutazione Periodo A</Typography>
                                <Typography variant="body1">{formattaData(datiRiepilogo.dataValutazionePeriodoA)}</Typography>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Sezione Valutazioni a scelta multipla */}
                    <Box mb={4}>
                        <Typography variant="h6" gutterBottom>
                            1. Le tue valutazioni
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <VisualizzaValutazione 
                            etichetta="Mi sento coinvolto/a e attivamente partecipe ai task che mi sono stati assegnati"
                            valore={datiRiepilogo.valutazioneCoinvolgimento}
                        />

                        <VisualizzaValutazione 
                            etichetta="Sto acquisendo nuove abilità professionali confrontate al momento in cui ho iniziato il tirocinio"
                            valore={datiRiepilogo.valutazioneNuoveAbilita}
                        />

                        <VisualizzaValutazione 
                            etichetta="Il carico di Lavoro è appropriato alle mie capacità e al tempo a disposizione"
                            valore={datiRiepilogo.valutazioneCaricoLavoro}
                        />

                        <VisualizzaValutazione 
                            etichetta="Ricevo istruzione chiare e dettagliate per implementare i miei task"
                            valore={datiRiepilogo.valutazioneIstruzioni}
                        />

                        <VisualizzaValutazione 
                            etichetta="I miei orari di Lavoro sono rispettati e ben organizzati"
                            valore={datiRiepilogo.valutazioneOrariLavoro}
                        />

                        <VisualizzaValutazione 
                            etichetta="La sistemazione abitativa assegnatami incontra le mie aspettative in termini di qualità e comfort"
                            valore={datiRiepilogo.valutazioneAbitazione}
                        />

                        <VisualizzaValutazione 
                            etichetta="La posizione dell'alloggio è comoda rispetto alla sede del tirocinio e ai servizi essenziali"
                            valore={datiRiepilogo.valutazionePosizioneAlloggio}
                        />

                        <VisualizzaValutazione 
                            etichetta="I trasporti locali per raggiungere il posto di Lavoro sono adeguati"
                            valore={datiRiepilogo.valutazioneTrasportiLocali}
                        />

                        <VisualizzaValutazione 
                            etichetta="L'organizzazione ricevente mi sta supportando efficacemente durante il periodo di tirocinio"
                            valore={datiRiepilogo.valutazioneSupporto}
                        />
                    </Box>

                    {/* Domande aperte */}
                    <Box mb={4}>
                        <Typography variant="h6" gutterBottom>
                            2. Cosa apprezzi di più rispetto all'azienda ospitante?
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <VisualizzaRisposta 
                            etichetta="La tua risposta:"
                            valore={datiRiepilogo.commentiPositivi}
                        />
                    </Box>

                    <Box mb={4}>
                        <Typography variant="h6" gutterBottom>
                            3. Costa non ti piace rispetto all'organizzazione delle attività presso l'azienda ospitante?
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <VisualizzaRisposta 
                            etichetta="La tua risposta:"
                            valore={datiRiepilogo.commentiNegativi}
                        />
                    </Box>

                    <Box mb={4}>
                        <Typography variant="h6" gutterBottom>
                            4. Altri commenti e suggerimenti
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <VisualizzaRisposta 
                            etichetta="La tua risposta:"
                            valore={datiRiepilogo.altriCommenti}
                        />
                    </Box>
                </Paper>
            )}

            {/* Pulsante per tornare alla homepage */}
            <Box textAlign="right" mt={2} mb={4}>
                <Button
                    onClick={tornaAllaHomepage}
                    variant="contained"
                    color="primary"
                    size="large"
                    className="pulsante-torna"
                >
                    Torna alla Homepage
                </Button>
            </Box>

            {/* Spinner di caricamento */}
            <Backdrop
                sx={{
                    color: '#ffffff',
                    zIndex: 999,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
                open={loading}
            >
                <CircularProgress
                    color="inherit"
                    size={100}
                    thickness={4}
                    style={{ marginBottom: '16px' }}
                />
                {messaggioCaricamento && (
                    <Typography variant="h6" component="div">
                        {messaggioCaricamento}
                    </Typography>
                )}
            </Backdrop>
        </Container>
    );
};

export default RiepilogoValutazioneIntermedia;